import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Lock, Bell, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { LeasingLinkCard } from '../components/leasing/LeasingLinkCard';

const roleBadges = {
    super_admin: { bg: 'bg-purple-500/20', text: 'text-purple-600', en: 'Super Admin', el: 'Υπερδιαχειριστής' },
    admin: { bg: 'bg-red-500/20', text: 'text-red-500', en: 'Administrator', el: 'Διαχειριστής' },
    fleet_manager: { bg: 'bg-[#1B7EB5]/20', text: 'text-[#1B7EB5]', en: 'Fleet Manager', el: 'Υπεύθυνος Στόλου' },
    driver: { bg: 'bg-green-500/20', text: 'text-green-600', en: 'Driver', el: 'Οδηγός' }
};

export default function SettingsPage() {
    const { user, refreshUser } = useAuth();
    const { language } = useLanguage();
    const el = language === 'el';

    const [profile, setProfile] = useState({ name: '', phone: '' });
    const [savingProfile, setSavingProfile] = useState(false);

    const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [savingPassword, setSavingPassword] = useState(false);

    const [prefs, setPrefs] = useState({ maintenance_alerts: true, checkin_alerts: true, damage_alerts: true });
    const [savingPrefs, setSavingPrefs] = useState(false);

    useEffect(() => {
        if (user) setProfile({ name: user.name || '', phone: user.phone || '' });
    }, [user]);

    useEffect(() => {
        api.get('/auth/notification-preferences')
            .then((res) => setPrefs(res.data))
            .catch(() => {});
    }, []);

    const handleSaveProfile = async () => {
        setSavingProfile(true);
        try {
            await api.put('/auth/profile', { name: profile.name, phone: profile.phone || null });
            await refreshUser();
            toast.success(el ? 'Το προφίλ ενημερώθηκε' : 'Profile updated');
        } catch (error) {
            toast.error(error.response?.data?.detail || (el ? 'Αποτυχία ενημέρωσης' : 'Update failed'));
        } finally {
            setSavingProfile(false);
        }
    };

    const handleChangePassword = async () => {
        if (!passwords.current || !passwords.next) {
            toast.error(el ? 'Συμπληρώστε τον τρέχοντα και τον νέο κωδικό' : 'Fill in current and new password');
            return;
        }
        if (passwords.next.length < 8) {
            toast.error(el ? 'Ο νέος κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες' : 'New password must be at least 8 characters');
            return;
        }
        if (passwords.next !== passwords.confirm) {
            toast.error(el ? 'Οι νέοι κωδικοί δεν ταιριάζουν' : 'New passwords do not match');
            return;
        }
        setSavingPassword(true);
        try {
            await api.post('/auth/change-password', {
                current_password: passwords.current,
                new_password: passwords.next
            });
            setPasswords({ current: '', next: '', confirm: '' });
            toast.success(el ? 'Ο κωδικός άλλαξε επιτυχώς' : 'Password changed successfully');
        } catch (error) {
            toast.error(error.response?.data?.detail || (el ? 'Αποτυχία αλλαγής κωδικού' : 'Password change failed'));
        } finally {
            setSavingPassword(false);
        }
    };

    const handleTogglePref = async (key) => {
        const updated = { ...prefs, [key]: !prefs[key] };
        setPrefs(updated);
        setSavingPrefs(true);
        try {
            await api.put('/auth/notification-preferences', updated);
        } catch (error) {
            setPrefs(prefs);
            toast.error(el ? 'Αποτυχία αποθήκευσης προτιμήσεων' : 'Failed to save preferences');
        } finally {
            setSavingPrefs(false);
        }
    };

    const badge = roleBadges[user?.role] || roleBadges.driver;

    const notificationRows = [
        {
            key: 'maintenance_alerts',
            title: el ? 'Ειδοποιήσεις Συντήρησης' : 'Maintenance Alerts',
            desc: el ? 'Υπενθυμίσεις για επερχόμενη συντήρηση, ΚΤΕΟ και καυσαέρια' : 'Reminders for upcoming maintenance, MOT and emissions'
        },
        {
            key: 'checkin_alerts',
            title: el ? 'Check-in / Check-out Οδηγών' : 'Driver Check-in / Check-out',
            desc: el ? 'Ενημέρωση όταν οδηγός παραλαμβάνει ή παραδίδει όχημα' : 'Get notified when a driver checks a vehicle in or out'
        },
        {
            key: 'damage_alerts',
            title: el ? 'Ειδοποιήσεις Ζημιών' : 'Damage Alerts',
            desc: el ? 'Αναφορές ζημιών και εντοπισμοί ζημιάς από AI' : 'Damage reports and AI-detected damage'
        }
    ];

    return (
        <div className="space-y-6 max-w-4xl" data-testid="settings-page">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">{el ? 'Ρυθμίσεις' : 'Settings'}</h1>
                <p className="text-zinc-500">{el ? 'Διαχειριστείτε τον λογαριασμό και τις προτιμήσεις σας' : 'Manage your account and preferences'}</p>
            </div>

            {['admin', 'fleet_manager'].includes(user?.role) && <LeasingLinkCard el={el} />}

            {/* Profile */}
            <div className="fleet-card p-6">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-[#1B7EB5]/10 rounded-sm flex items-center justify-center">
                        <User className="w-5 h-5 text-[#1B7EB5]" />
                    </div>
                    <div>
                        <h2 className="text-base md:text-lg font-semibold text-slate-800">{el ? 'Στοιχεία Προφίλ' : 'Profile Information'}</h2>
                        <p className="text-sm text-zinc-500">{el ? 'Ενημερώστε τα προσωπικά σας στοιχεία' : 'Update your personal details'}</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="form-group">
                        <Label className="form-label">{el ? 'Ονοματεπώνυμο' : 'Full Name'}</Label>
                        <Input
                            value={profile.name}
                            onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                            className="bg-white border-slate-300 text-slate-900"
                            data-testid="settings-name-input"
                        />
                    </div>
                    <div className="form-group">
                        <Label className="form-label">Email</Label>
                        <Input
                            value={user?.email || ''}
                            className="bg-white border-slate-300 text-slate-900"
                            disabled
                            data-testid="settings-email-input"
                        />
                    </div>
                    <div className="form-group">
                        <Label className="form-label">{el ? 'Τηλέφωνο' : 'Phone'}</Label>
                        <Input
                            value={profile.phone}
                            onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                            placeholder={el ? 'π.χ. 6912345678' : 'e.g. 6912345678'}
                            className="bg-white border-slate-300 text-slate-900"
                            data-testid="settings-phone-input"
                        />
                    </div>
                    <div className="form-group">
                        <Label className="form-label">{el ? 'Ρόλος' : 'Role'}</Label>
                        <div className="flex items-center h-10">
                            <span className={`px-2 py-1 rounded-sm ${badge.bg} ${badge.text} text-sm`}>
                                {el ? badge.el : badge.en}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end mt-6">
                    <Button
                        onClick={handleSaveProfile}
                        disabled={savingProfile}
                        className="bg-[#1B7EB5] hover:bg-[#58A6B4] text-white rounded-sm px-6 transition-colors"
                        data-testid="settings-save-profile-btn"
                    >
                        {savingProfile
                            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{el ? 'Αποθήκευση...' : 'Saving...'}</>
                            : (el ? 'Αποθήκευση Προφίλ' : 'Save Profile')}
                    </Button>
                </div>
            </div>

            {/* Security */}
            <div className="fleet-card p-6">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-red-500/10 rounded-sm flex items-center justify-center">
                        <Lock className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                        <h2 className="text-base md:text-lg font-semibold text-slate-800">{el ? 'Ασφάλεια' : 'Security'}</h2>
                        <p className="text-sm text-zinc-500">{el ? 'Αλλάξτε τον κωδικό πρόσβασής σας' : 'Update your password'}</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="form-group">
                        <Label className="form-label">{el ? 'Τρέχων Κωδικός' : 'Current Password'}</Label>
                        <div className="relative">
                            <Input
                                type={showCurrentPassword ? 'text' : 'password'}
                                value={passwords.current}
                                onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                                placeholder={el ? 'Εισάγετε τον τρέχοντα κωδικό' : 'Enter current password'}
                                className="bg-white border-slate-300 text-slate-900 pr-10"
                                data-testid="settings-current-password-input"
                            />
                            <button
                                type="button"
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                data-testid="toggle-current-password-visibility"
                            >
                                {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>
                    <div></div>
                    <div className="form-group">
                        <Label className="form-label">{el ? 'Νέος Κωδικός' : 'New Password'}</Label>
                        <div className="relative">
                            <Input
                                type={showNewPassword ? 'text' : 'password'}
                                value={passwords.next}
                                onChange={(e) => setPasswords({ ...passwords, next: e.target.value })}
                                placeholder={el ? 'Τουλάχιστον 8 χαρακτήρες' : 'At least 8 characters'}
                                className="bg-white border-slate-300 text-slate-900 pr-10"
                                data-testid="settings-new-password-input"
                            />
                            <button
                                type="button"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                data-testid="toggle-new-password-visibility"
                            >
                                {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>
                    <div className="form-group">
                        <Label className="form-label">{el ? 'Επιβεβαίωση Νέου Κωδικού' : 'Confirm New Password'}</Label>
                        <div className="relative">
                            <Input
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={passwords.confirm}
                                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                                placeholder={el ? 'Επαναλάβετε τον νέο κωδικό' : 'Confirm new password'}
                                className="bg-white border-slate-300 text-slate-900 pr-10"
                                data-testid="settings-confirm-password-input"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                data-testid="toggle-confirm-password-visibility"
                            >
                                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end mt-6">
                    <Button
                        onClick={handleChangePassword}
                        disabled={savingPassword}
                        className="bg-red-500 hover:bg-red-600 text-white rounded-sm px-6 transition-colors"
                        data-testid="settings-change-password-btn"
                    >
                        {savingPassword
                            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{el ? 'Αλλαγή...' : 'Changing...'}</>
                            : (el ? 'Αλλαγή Κωδικού' : 'Change Password')}
                    </Button>
                </div>
            </div>

            {/* Notifications */}
            <div className="fleet-card p-6">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-blue-500/10 rounded-sm flex items-center justify-center">
                        <Bell className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                        <h2 className="text-base md:text-lg font-semibold text-slate-800">{el ? 'Ειδοποιήσεις' : 'Notifications'}</h2>
                        <p className="text-sm text-zinc-500">
                            {el ? 'Επιλέξτε ποιες ειδοποιήσεις θέλετε να λαμβάνετε' : 'Choose which notifications you receive'}
                            {savingPrefs && (el ? ' · Αποθήκευση...' : ' · Saving...')}
                        </p>
                    </div>
                </div>

                <div className="space-y-1">
                    {notificationRows.map((row, i) => (
                        <div
                            key={row.key}
                            className={`flex items-center justify-between py-4 ${i < notificationRows.length - 1 ? 'border-b border-slate-200' : ''}`}
                        >
                            <div>
                                <p className="text-slate-800 font-medium">{row.title}</p>
                                <p className="text-sm text-zinc-500">{row.desc}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={!!prefs[row.key]}
                                    onChange={() => handleTogglePref(row.key)}
                                    className="sr-only peer"
                                    data-testid={`settings-toggle-${row.key}`}
                                />
                                <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-[#1B7EB5]"></div>
                            </label>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
